import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { BackendCN54BisData } from '../types';

export interface CN54bisReportParams {
  ppoValue?: string;
  direction: string;
  year: string;
  formatType: 'html' | 'pdf' | 'xls';
}


export interface CN54bisReportResponse {
  // For HTML format, contains the table data - backend returns array of BackendCN54BisData
  data?: BackendCN54BisData[];
  url?: string;
  content?: Blob;
  filename?: string; // Add filename field for PDF/XLS downloads
}

export function useCN54bisReport() {
  return useMutation<CN54bisReportResponse, Error, CN54bisReportParams>({
    mutationFn: async (params: CN54bisReportParams) => {
      // Create URLSearchParams to handle parameters
      const searchParams = new URLSearchParams();
      
      // Add direction (convert to uppercase as API expects INBOUND/OUTBOUND)
      searchParams.append('table', params.direction.toUpperCase());
      
      // Add year
      searchParams.append('year', params.year);
      
      // Add format type
      searchParams.append('formatType', params.formatType);
      
      // Add PPO value (send "all" if no specific operator is selected)
      searchParams.append('designatedOperator', params.ppoValue || 'all');

      const response = await axios.get(`/reports/cn54bis?${searchParams.toString()}`, {
        // HTML returns JSON with table data, PDF/XLS return binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
      });

      // For HTML format, return the table data
      if (params.formatType === 'html') {
        return { data: response.data };
      }

      // For PDF/XLS, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `CN54bis_Report_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = params.formatType === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        content: response.data,
        filename: fileName
      };
    },
  });
}
